
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CloudRain, Car, Zap, Eye, Loader2, Cloud, Sun, CloudLightning, AlertOctagon, Info, MapPin, ChevronDown, Check, Radar, Navigation, Activity, TrendingUp, TrendingDown, Users, Package, HelpCircle, Calculator, ThermometerSun, Timer, BookOpen, RefreshCw, Moon, MapPinned } from 'lucide-react';
import { OperatingContext, SystemConfig, Order, Driver } from '../types';
import { getCurrentWeather } from '../services/weatherService';
import { scanTrafficRadar } from '../services/radarService';
import { calculateSystemPulse, setCacheEntry, TTL_CONTEXT } from '../services/parserService';
import { calculateServiceTimeMetrics } from '../services/logistics/fleet/telemetry';
import { toast } from 'react-hot-toast';
import { checkGoogleMapsAvailability, loadGoogleMaps, onGoogleMapsAuthFailure } from '../services/googleMapsLoader';
import { useSal } from '../contexts/SalContext';

interface Props {
    context: OperatingContext;
    setContext: React.Dispatch<React.SetStateAction<OperatingContext>>;
    config: SystemConfig;
    setConfig: (cfg: SystemConfig) => Promise<void> | void;
    orders: Order[];
    drivers: Driver[];
}

export const ContextBar: React.FC<Props> = ({ context, setContext, config, setConfig, orders, drivers }) => {
    const { jarvisActions } = useSal();
    const [loadingWeather, setLoadingWeather] = useState(false);
    const [isRefreshingCoords, setIsRefreshingCoords] = useState(false);
    const [showCoordsTooltip, setShowCoordsTooltip] = useState(false);
    const [showWeatherDetails, setShowWeatherDetails] = useState(false);
    const [isRainMenuOpen, setIsRainMenuOpen] = useState(false);
    const [isScanningRadar, setIsScanningRadar] = useState(false);
    const [showRadarDetails, setShowRadarDetails] = useState(false);
    const isGoogleMapsApiLoadedForRadarRef = useRef(false);
    const [showPulseDetails, setShowPulseDetails] = useState(false);
    const [showServiceTimeDetails, setShowServiceTimeDetails] = useState(false);

    // v59.0: Conta pedidos sem coordenadas
    const ordersWithoutCoords = useMemo(() => {
        return orders.filter(o => o.type === 'DELIVERY' && (!o.lat || !o.lng));
    }, [orders]);

    const handleRefreshCoordinates = useCallback(async () => {
        if (isRefreshingCoords || ordersWithoutCoords.length === 0) return;
        setIsRefreshingCoords(true);
        try {
            await jarvisActions.refreshMissingCoordinates();
        } finally {
            setIsRefreshingCoords(false);
            setShowCoordsTooltip(false);
        }
    }, [jarvisActions, ordersWithoutCoords.length, isRefreshingCoords]);

    const pulse = useMemo(() => calculateSystemPulse(orders, drivers, config), [orders, drivers, config]);

    const activeTriageCount = useMemo(() => {
        return orders.filter(o => ['pending', 'payment_pending', 'confirmed', 'ready', 'scheduled'].includes(o.status)).length;
    }, [orders]);

    useEffect(() => {
        setCacheEntry('sal_context_cache_v1', 'current', context, TTL_CONTEXT);
    }, [context]);

    const fetchWeather = useCallback(async () => {
        if (context.weather && !loadingWeather) return;

        setLoadingWeather(true);
        const weather = await getCurrentWeather(config.businessLat, config.businessLng, config.hgWeatherApiKey);
        if (weather) setContext(prev => ({ ...prev, weather }));
        setLoadingWeather(false);
    }, [config.businessLat, config.businessLng, config.hgWeatherApiKey, context.weather, setContext]);

    useEffect(() => {
        fetchWeather();
        const interval = setInterval(fetchWeather, 900000); // 15 minutos
        return () => clearInterval(interval);
    }, [fetchWeather]);

    const triggerRadarScan = useCallback(async (isAuto = false) => {
        if (!config.googleMapsApiKey || !isGoogleMapsApiLoadedForRadarRef.current || !checkGoogleMapsAvailability()) return;

        if (isAuto && activeTriageCount === 0) return;

        if (!isAuto && context.radar?.lastScan) {
            const elapsed = Date.now() - context.radar.lastScan;
            if (elapsed < 1200000) {
                const remaining = Math.ceil((1200000 - elapsed) / 60000);
                toast(`Radar recente. Aguarde ${remaining}min.`, { icon: '⏳' });
                return;
            }
        }

        setIsScanningRadar(true);
        try {
            const radarData = await scanTrafficRadar(config.businessLat, config.businessLng, config.googleMapsApiKey);
            if (radarData) {
                setContext(prev => ({ ...prev, radar: radarData }));
                if (!isAuto) toast.success(`Trânsito: ${radarData.trafficIndex.toFixed(2)}x`, { icon: '📡' });
            }
        } catch (e) {
            console.error("Radar scan failed", e);
        } finally { setIsScanningRadar(false); }
    }, [config.googleMapsApiKey, config.businessLat, config.businessLng, context.radar, activeTriageCount, setContext]);

    useEffect(() => {
        let isMounted = true;
        const ensureGoogleMaps = async () => {
            if (config.googleMapsApiKey && !isGoogleMapsApiLoadedForRadarRef.current) {
                try {
                    await loadGoogleMaps(config.googleMapsApiKey);
                    if (isMounted && checkGoogleMapsAvailability()) {
                        isGoogleMapsApiLoadedForRadarRef.current = true;
                        if (!context.radar && activeTriageCount > 0) triggerRadarScan(true);
                    }
                } catch (e) { }
            }
        };
        ensureGoogleMaps();
        const hourlyInterval = setInterval(() => triggerRadarScan(true), 3600000);
        const unsubscribe = onGoogleMapsAuthFailure(() => { if (isMounted) isGoogleMapsApiLoadedForRadarRef.current = false; });
        return () => { isMounted = false; unsubscribe(); clearInterval(hourlyInterval); };
    }, [config.googleMapsApiKey, triggerRadarScan, context.radar, activeTriageCount]);

    const prevActiveCountRef = useRef(activeTriageCount);
    useEffect(() => {
        if (prevActiveCountRef.current === 0 && activeTriageCount > 0) {
            const lastScan = context.radar?.lastScan || 0;
            if (Date.now() - lastScan > 1200000) {
                triggerRadarScan(true);
            }
        }
        prevActiveCountRef.current = activeTriageCount;
    }, [activeTriageCount, triggerRadarScan, context.radar]);

    const selectRainMode = (mode: 'off' | 'light' | 'heavy') => {
        setContext(prev => ({ ...prev, rainMode: mode }));
        setIsRainMenuOpen(false);
    };

    const handleManualTrafficToggle = () => {
        if (!context.isHighTraffic) triggerRadarScan(false);
        setContext(prev => ({ ...prev, isHighTraffic: !prev.isHighTraffic }));
    };

    const routingRecommendationMode = config.routingRecommendationMode === 'simplified' ? 'simplified' : 'advanced';

    const handleToggleRoutingRecommendationMode = useCallback(() => {
        const nextMode = routingRecommendationMode === 'advanced' ? 'simplified' : 'advanced';
        setConfig({ ...config, routingRecommendationMode: nextMode });
        toast.success(nextMode === 'simplified' ? 'Modo simplificado ativado.' : 'Modo avancado ativado.');
    }, [routingRecommendationMode, setConfig, config]);

    const serviceMetrics = useMemo(() => calculateServiceTimeMetrics(orders), [orders]);

    const renderWeatherWidget = () => {
        if (loadingWeather && !context.weather) return <div className="flex items-center gap-2 px-3 py-1 bg-card-bg/50 rounded-full border border-border-dim opacity-60"><Loader2 className="w-3 h-3 animate-spin text-icon-muted" /><span className="text-xs font-bold text-text-muted uppercase-ops tracking-wide">Sondando...</span></div>;
        
        // Se não há dados do clima, mostra estado "sem dados" mas ainda visível
        if (!context.weather) return (
            <div className="relative" onMouseEnter={() => setShowWeatherDetails(true)} onMouseLeave={() => setShowWeatherDetails(false)}>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-card-border bg-card-bg/40 text-card-text-muted transition-all shadow-sm cursor-help opacity-80"><Cloud className="w-3 h-3" /><span className="text-xs font-bold text-text-muted uppercase-ops tracking-wide">Clima: --</span></div>
                {showWeatherDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-52 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-3 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex items-center gap-2 mb-2 border-b border-border-dim pb-2">
                            <Info className="w-4 h-4 text-status-pending" />
                            <span className="text-xs font-black text-tooltip-text uppercase-ops tracking-widest">Clima Indisponível</span>
                        </div>
                        <p className="text-xs text-text-muted leading-relaxed mb-3">Configure a API do HG Weather para ativar o monitoramento climático.</p>
                        <button onClick={() => fetchWeather()} className="w-full py-2 bg-sal-cyber-teal/20 text-sal-cyber-teal text-xs font-black uppercase-ops tracking-wide rounded-lg hover:bg-sal-cyber-teal/30 transition-all">
                            Tentar Novamente
                        </button>
                    </div>
                )}
            </div>
        );
        
        const { temp, conditionText, alertLevel } = context.weather;
        let badgeColor = 'bg-card-bg text-card-text-muted border-card-border';
        let Icon = Sun;
        if (alertLevel === 'warning') { badgeColor = 'bg-status-pending/10 text-status-pending border-status-pending/30 animate-pulse'; Icon = Cloud; }
        else if (alertLevel === 'critical') { badgeColor = 'bg-status-critical/10 text-status-critical border-status-critical/30 animate-pulse'; Icon = CloudLightning; }
        return (
            <div className="relative" onMouseEnter={() => setShowWeatherDetails(true)} onMouseLeave={() => setShowWeatherDetails(false)}>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${badgeColor} transition-all shadow-sm cursor-help`}><Icon className="w-3 h-3" /><span className="text-xs font-bold text-sal-cyber-teal">{temp}°</span><span className="text-xs font-bold uppercase-ops hidden xl:inline border-l border-border-dim/50 pl-1.5 ml-0.5 opacity-80 tracking-wide">{conditionText}</span></div>
                {showWeatherDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-52 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-3 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex justify-between items-center mb-2 border-b border-border-dim pb-2">
                            <span className="text-xs font-black text-tooltip-text uppercase-ops tracking-widest">Sentinela Climático</span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-tooltip-text uppercase-ops truncate max-w-[120px]">{context.weather.cityName}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-black uppercase-ops ${alertLevel === 'normal' ? 'bg-status-ready/20 text-status-ready' : alertLevel === 'warning' ? 'bg-status-pending/20 text-status-pending' : 'bg-status-critical/20 text-status-critical'}`}>
                                    {alertLevel === 'normal' ? 'Estável' : 'Alerta'}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <div className="bg-black/5 dark:bg-white/5 p-2 rounded-lg border border-border-dim/20">
                                    <div className="text-[10px] text-text-muted font-bold uppercase-ops mb-0.5">Temperatura</div>
                                    <div className="text-sm font-mono font-bold text-tooltip-text flex items-center gap-1">
                                        <ThermometerSun className="w-3 h-3 text-sal-cyber-teal" />{temp}°C
                                    </div>
                                </div>
                                <div className="bg-black/5 dark:bg-white/5 p-2 rounded-lg border border-border-dim/20">
                                    <div className="text-[10px] text-text-muted font-bold uppercase-ops mb-0.5">Condição</div>
                                    <div className="text-[10px] font-bold text-text-muted leading-tight">{conditionText}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderRadarWidget = () => {
        if (isScanningRadar) return <div className="flex items-center gap-2 px-3 py-1 bg-app-bg/50 rounded-full border border-border-dim opacity-60"><Loader2 className="w-3 h-3 animate-spin text-icon-muted" /><span className="text-xs font-bold text-text-muted uppercase-ops tracking-wide">Radar...</span></div>;
        const isIdle = activeTriageCount === 0;
        if (isIdle) return (
            <div className="relative" onMouseEnter={() => setShowRadarDetails(true)} onMouseLeave={() => setShowRadarDetails(false)}>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-card-border bg-card-bg/40 text-card-text-muted transition-all shadow-sm cursor-help opacity-60"><Moon className="w-3 h-3" /><span className="text-xs font-black uppercase-ops tracking-wide">Radar: Dormindo</span></div>
                {showRadarDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-56 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-4 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex items-center gap-2 mb-2 text-status-info">
                            <Zap className="w-4 h-4" />
                            <span className="text-xs font-black text-tooltip-text uppercase-ops tracking-widest">Protocolo Zero-Waste</span>
                        </div>
                        <p className="text-xs text-text-muted leading-relaxed">A Sofia desligou os sensores de trânsito para economizar API, já que o Hub está vazio.</p>
                    </div>
                )}
            </div>
        );

        // Se não há dados do radar, mostra estado de "sem dados" mas ainda visível
        if (!context.radar) return (
            <div className="relative" onMouseEnter={() => setShowRadarDetails(true)} onMouseLeave={() => setShowRadarDetails(false)}>
                <div onClick={() => triggerRadarScan(false)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-card-border bg-card-bg/40 text-card-text-muted transition-all shadow-sm cursor-pointer hover:brightness-110 active:scale-95"><Radar className="w-3 h-3" /><span className="text-xs font-black uppercase-ops tracking-wide">Trânsito: --</span></div>
                {showRadarDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-56 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-4 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex items-center gap-2 mb-2 text-status-pending">
                            <AlertOctagon className="w-4 h-4" />
                            <span className="text-xs font-black text-tooltip-text uppercase-ops tracking-widest">Radar Indisponível</span>
                        </div>
                        <p className="text-xs text-text-muted leading-relaxed mb-3">Clique para tentar novamente ou configure a API do Google Maps.</p>
                        <button onClick={(e) => { e.stopPropagation(); triggerRadarScan(false); }} className="w-full py-2 bg-sal-cyber-teal/20 text-sal-cyber-teal text-xs font-black uppercase-ops tracking-wide rounded-lg hover:bg-sal-cyber-teal/30 transition-all">
                            Ativar Radar
                        </button>
                    </div>
                )}
            </div>
        );

        const { trafficIndex, congestionLevel, details } = context.radar;
        let color = 'text-status-ready border-status-ready/30 bg-status-ready/10';
        if (congestionLevel === 'moderate') color = 'text-status-pending border-status-pending/30 bg-status-pending/10';
        else if (congestionLevel === 'high') color = 'text-status-warning border-status-warning/30 bg-status-warning/10';
        else if (congestionLevel === 'critical') color = 'text-status-critical border-status-critical/30 bg-status-critical/10 animate-pulse';

        return (
            <div className="relative" onMouseEnter={() => setShowRadarDetails(true)} onMouseLeave={() => setShowRadarDetails(false)}>
                <div onClick={() => triggerRadarScan(false)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${color} transition-all shadow-sm cursor-pointer hover:brightness-110 active:scale-95`}><Radar className="w-3 h-3" /><span className="text-xs font-black uppercase-ops tracking-wide">Trânsito: {trafficIndex.toFixed(2)}x</span></div>
                {showRadarDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-56 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-4 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex justify-between items-center mb-3 border-b border-border-dim pb-2">
                            <span className="text-xs font-black text-tooltip-text uppercase-ops tracking-widest">Radar Direcional</span>
                            <button onClick={() => triggerRadarScan(false)} className="p-1 bg-black/5 dark:bg-white/5 rounded hover:bg-black/10 dark:hover:bg-white/10 text-sm">
                                <RefreshCw className="w-3 h-3 text-icon-muted" />
                            </button>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                {Object.entries(details).map(([dir, rawFactor]) => (
                                    <div key={dir} className="bg-black/5 dark:bg-white/5 p-2 rounded-lg border border-border-dim/20">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] text-text-muted font-bold uppercase-ops">{dir === 'N' ? 'Norte' : dir === 'S' ? 'Sul' : dir === 'E' ? 'Leste' : 'Oeste'}</span>
                                            <div className={`w-1 h-1 rounded-full ${(rawFactor as number) > 1.4 ? 'bg-status-critical' : (rawFactor as number) > 1.1 ? 'bg-status-pending' : 'bg-status-ready'}`} />
                                        </div>
                                        <div className="text-sm font-mono font-bold text-tooltip-text">{(rawFactor as number).toFixed(2)}x</div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-[10px] text-text-muted text-center italic mt-1 font-bold">Gasto inteligente: Radar dorme se hub ocioso.</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderServiceTimeWidget = () => {
        const isFallback = serviceMetrics.last30Days === 0;
        const activeTime = isFallback ? (config.fleetConfig.serviceTimeMinutes || 3) : serviceMetrics.last30Days;
        return (
            <div className="relative" onMouseEnter={() => setShowServiceTimeDetails(true)} onMouseLeave={() => setShowServiceTimeDetails(false)}>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all shadow-sm cursor-help ${isFallback ? 'border-card-border border-dashed opacity-80' : 'border-status-info/30 bg-status-info/10'}`}><Timer className="w-3 h-3 text-status-info" /><span className={`text-xs font-black uppercase-ops tracking-wide ${isFallback ? 'text-card-text-muted' : 'text-card-text'}`}>Serviço: {activeTime.toFixed(1)}m</span></div>
                {showServiceTimeDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-64 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-4 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex justify-between items-center mb-3 border-b border-border-dim pb-2">
                            <span className="text-xs font-black text-status-info uppercase-ops tracking-widest">{isFallback ? 'Estimativa (Manual)' : 'Pulso de Entrega (Real)'}</span>
                        </div>
                        <div className="space-y-3">
                            <div className={`p-2 rounded-lg border ${isFallback ? 'bg-black/5 dark:bg-white/5 border-border-dim/30' : 'bg-status-info/10 border-status-info/20'}`}>
                                <div className="flex justify-between items-center">
                                    <span className={`text-xs font-black uppercase-ops ${isFallback ? 'text-text-muted' : 'text-status-info'}`}>{isFallback ? 'Configuração' : 'Média 30 Dias'}</span>
                                    <span className="text-base font-mono font-black text-tooltip-text">{activeTime.toFixed(1)}m</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderPulseWidget = () => {
        const load = pulse.loadFactor;
        const capacity = config.fleetConfig.maxOrdersPerRoute || 4;
        const statusColor = load < 1.0 ? 'bg-status-ready/10 text-status-ready border-status-ready/20' : load <= capacity ? 'bg-sal-cyber-teal/10 text-sal-cyber-teal border-sal-cyber-teal/20' : 'bg-status-warning/10 text-status-warning border-status-warning/20';
        return (
            <div className="relative" onMouseEnter={() => setShowPulseDetails(true)} onMouseLeave={() => setShowPulseDetails(false)}>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all ${statusColor} shadow-sm`}><Activity className="w-3 h-3" /><span className="text-xs font-bold uppercase-ops tracking-wide">PULSO</span></div>
                {showPulseDetails && (
                    <div className="absolute top-full left-0 mt-2 z-[70] w-56 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-3 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                        <div className="flex justify-between items-center mb-2 border-b border-border-dim pb-2">
                            <span className="text-xs font-black text-sal-cyber-teal uppercase-ops tracking-widest">Pulso do Hub</span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between text-xs font-bold">
                                <span className="text-text-muted uppercase-ops">Equipe On</span>
                                <span className="text-tooltip-text font-mono">{pulse.activeDrivers}</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold">
                                <span className="text-text-muted uppercase-ops">Pendentes</span>
                                <span className="text-tooltip-text font-mono">{pulse.pendingCount}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const rainFee = context.rainMode === 'light' ? config.rainSurchargeLight : config.rainSurchargeHeavy;
    const rainColor = context.rainMode === 'off' ? 'bg-card-bg text-text-muted border-border-dim hover:bg-app-bg' : context.rainMode === 'light' ? 'bg-status-info text-badge-text-inv border-status-info/50' : 'bg-status-alert text-badge-text-inv border-status-alert/50';

    return (
        <div className="bg-app-bg border-b border-white/5 px-2 xl:px-3 py-1 xl:py-1.5 flex items-center justify-between shadow-sm z-[60] overflow-visible gap-1 xl:gap-2 relative">
            <div className="flex items-center gap-1 xl:gap-1.5 shrink-0 relative">
                <span className="text-xs font-black text-text-muted uppercase-ops tracking-widest mr-1 hidden md:inline">Monitor:</span>
                {renderWeatherWidget()}
                <div className="w-px h-4 bg-white/[0.03] mx-1 hidden sm:block"></div>
                {renderRadarWidget()}
                <div className="w-px h-4 bg-white/[0.03] mx-1 hidden sm:block"></div>
                {renderServiceTimeWidget()}
                <div className="w-px h-4 bg-white/[0.03] mx-1 hidden sm:block"></div>
                {renderPulseWidget()}
            </div>
            <div className="flex items-center gap-1.5 shrink-0 overflow-visible">
                <button
                    type="button"
                    onClick={handleToggleRoutingRecommendationMode}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black uppercase-ops tracking-wide shadow-sm border transition-all ${routingRecommendationMode === 'simplified' ? 'bg-status-info text-badge-text-inv border-status-info/50' : 'bg-card-bg text-text-muted border-border-dim hover:bg-app-bg'}`}
                    title={routingRecommendationMode === 'simplified' ? 'Modo simplificado ativo: previsivel e deterministico' : 'Modo avancado ativo: arbitragem completa'}
                    aria-label="Alternar modo de recomendacao"
                >
                    <Navigation className="w-3 h-3" />
                    <span className="hidden lg:inline">Roteamento</span>
                    <span className="text-[10px] bg-black/20 px-1.5 py-0.5 rounded">{routingRecommendationMode === 'simplified' ? 'SIMPLIFICADO' : 'AVANCADO'}</span>
                </button>
                {/* v59.0: Botão de coordenadas */}
                {ordersWithoutCoords.length > 0 && (
                    <div className="relative">
                        <button
                            type="button"
                            onClick={handleRefreshCoordinates}
                            onMouseEnter={() => setShowCoordsTooltip(true)}
                            onMouseLeave={() => setShowCoordsTooltip(false)}
                            disabled={isRefreshingCoords}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black transition-all uppercase-ops tracking-wide shadow-sm border ${isRefreshingCoords ? 'bg-sal-cyber-teal/20 text-sal-cyber-teal border-sal-cyber-teal/30' : 'bg-status-warning/10 text-status-warning border-status-warning/30 hover:bg-status-warning/20'}`}
                        >
                            {isRefreshingCoords ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPinned className="w-3 h-3" />}
                            <span className="hidden sm:inline">{ordersWithoutCoords.length} sem GPS</span>
                        </button>
                        {showCoordsTooltip && (
                            <div className="absolute top-full right-0 mt-2 z-[70] w-48 bg-tooltip-bg/90 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-border-dim/40 backdrop-blur-xl p-3 animate-in fade-in slide-in-from-top-2 ring-1 ring-white/5">
                            <div className="flex items-center gap-2 mb-2 border-b border-border-dim pb-2">
                                <MapPinned className="w-4 h-4 text-status-warning" />
                                <span className="text-xs font-black text-tooltip-text uppercase-ops tracking-widest">Coordenadas Faltando</span>
                            </div>
                            <p className="text-xs text-text-muted leading-relaxed mb-2">{ordersWithoutCoords.length} pedido(s) sem coordenadas. Clique para geocodificar.</p>
                            <button
                                type="button"
                                onClick={handleRefreshCoordinates}
                                disabled={isRefreshingCoords}
                                className="w-full py-2 bg-status-warning/20 text-status-warning text-xs font-black uppercase-ops tracking-wide rounded-lg hover:bg-status-warning/30 transition-all disabled:opacity-50"
                            >
                                {isRefreshingCoords ? 'Processando...' : 'Geocodificar Agora'}
                            </button>
                        </div>
                        )}
                    </div>
                )}
                <div className="relative"><button type="button" onClick={() => setIsRainMenuOpen(!isRainMenuOpen)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black transition-all uppercase-ops tracking-wide ${rainColor} shadow-sm border border-border-dim`}>{context.rainMode === 'heavy' ? <CloudLightning className="w-3 h-3" /> : <CloudRain className="w-3 h-3" />}<span className="hidden sm:inline">{context.rainMode === 'off' ? 'Tempo Limpo' : context.rainMode === 'light' ? 'Chuva Leve' : 'Chuva Pesada'}</span>{context.rainMode !== 'off' && (<span className="text-xs bg-black/20 px-1 rounded ml-1">+{rainFee}%</span>)}<ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${isRainMenuOpen ? 'rotate-180' : ''}`} /></button>{isRainMenuOpen && (<div className="absolute top-full right-0 mt-2 w-48 bg-card-bg rounded-xl shadow-2xl border border-border-dim z-[70] overflow-hidden flex flex-col p-1 animate-in fade-in slide-in-from-top-2 ring-1 ring-sal-cyber-teal/20"><button type="button" onClick={() => selectRainMode('off')} className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-bold text-text-muted hover:bg-app-bg/50"><div className="flex items-center gap-2"><Sun className="w-4 h-4 text-status-warning" /><span>TEMPO LIMPO</span></div></button><button type="button" onClick={() => selectRainMode('light')} className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-bold text-text-muted hover:bg-app-bg/50"><div className="flex items-center gap-2"><CloudRain className="w-4 h-4 text-status-info" /><span>CHUVA LEVE</span></div></button><button type="button" onClick={() => selectRainMode('heavy')} className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-bold text-text-muted hover:bg-app-bg/50"><div className="flex items-center gap-2"><CloudLightning className="w-4 h-4 text-status-alert" /><span>CHUVA PESADA</span></div></button></div>)}</div>
                <button type="button" onClick={handleManualTrafficToggle} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black transition-all uppercase-ops tracking-wide ${context.isHighTraffic ? 'bg-status-critical text-badge-text-inv' : 'bg-card-bg text-text-muted border border-border-dim'} shadow-sm`}><Car className="w-3 h-3" /><span className="hidden sm:inline">{context.isHighTraffic ? 'Trânsito Intenso' : 'Fluxo Normal'}</span></button>
            </div>
        </div>
    );
};
